import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { connect } from 'dva';

import { Typography } from '@hulu/react-style-components';
import { FormWrapper, PermissionManagerWrapper, Y_SCROLL } from './styles';
import { PERMISSION_MANAGER_TABLE, PERMISSION_MANAGER_VIEW_ROLES } from '../../testUtils';
import { DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import { convertUserDataToTableData } from './utils';
import GridLayout from '../Templates/GridLayout';
import { ButtonWrapper, LeftSideHeaderWrapper } from '../AdsTracer/Header/styles';
import { Form, Input, List, Modal, Row, Select, Spin } from 'antd';
import NewUserModal from './NewUser';
import LeftPanel from './LeftPanel';

const ViewUsers = ({ dispatch, userData = [], permissionWidths = [], roles = [], loading }) => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserRoles, setSelectedUserRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [userRoles, setUserRoles] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [form] = Form.useForm();
  const LEFT_HAND_SPAN = 10;
  const RIGHT_HAND_SPAN = 14;

  const tableData = useMemo(() => Object.entries(userData).map(convertUserDataToTableData), [userData]);

  const memoizedPermissionWidths = useMemo(() => permissionWidths, [permissionWidths]);

  const handleWidthChange = useCallback(
    (widths) =>
      dispatch({
        type: 'permissionManager/updatePermissionManagerWidths',
        payload: widths,
      }),
    [dispatch]
  );
  const handleSelectedUser = useCallback(
    (selectedRowKeys, selectedRows) => {
      let rolesForUser = selectedRows[0]?.roles || [];
      setSelectedUser(selectedRowKeys[0]);
      if (typeof rolesForUser === 'string') {
        rolesForUser = rolesForUser.split(',').map((role) => role.trim());
      }
      setSelectedUserRoles(rolesForUser);

      const selectedUserRoleObjects = rolesForUser
        .map((roleName) => (roles || []).find((role) => role.name === roleName && role.id && role.name))
        .filter(Boolean);
      setUserRoles(selectedUserRoleObjects);
    },
    [roles]
  );

  const handleUserRolesUpdate = (email, updatedRoles) => {
    const updatedUserData = userData.map((user) =>
      user['email-id'] === email ? { ...user, roles: updatedRoles } : user
    );
    dispatch({
      type: 'permissionManager/updateUserData',
      payload: updatedUserData,
    });
  };
  const filteredRoles = selectedUserRoles.filter((role) => role.toLowerCase().includes(searchTerm.toLowerCase()));
  const showModal = () => {
    setIsModalVisible(true);
  };
  // Move mapping inside the function
  const handleAddRole = () => {
    const selectedUserRoleObjects = selectedUserRoles
      .map((roleName) => roles.find((role) => role.name === roleName && role.id && role.name))
      .filter(Boolean);

    if (selectedRole && !userRoles.some((role) => role.name === selectedRole)) {
      const roleToAdd = roles.find((role) => role.name === selectedRole);
      if (roleToAdd && roleToAdd.id && roleToAdd.name) {
        const updatedRoles = [...selectedUserRoleObjects, ...userRoles, roleToAdd].filter(
          (role, index, self) => self.findIndex((r) => r.id === role.id) === index
        );
        setUserRoles(updatedRoles);
        setSelectedUserRoles(updatedRoles.map((role) => role.name));
        setSelectedRole(null);
        handleUserRolesUpdate(
          selectedUser,
          updatedRoles.map((role) => role.name)
        );
        dispatch({
          type: 'permissionManager/updateUser',
          payload: {
            email: selectedUser,
            roles: updatedRoles.filter((role) => role.id).map((role) => ({ id: role.id })),
          },
        });
      } else {
        console.error('Invalid role object:', roleToAdd);
      }
    }
  };

  const handleDeleteRole = (roleName) => {
    const updatedRoles = userRoles.filter((role) => role.name !== roleName);
    setUserRoles(updatedRoles);
    setSelectedUserRoles(updatedRoles.map((role) => role.name));
    handleUserRolesUpdate(
      selectedUser,
      updatedRoles.map((role) => role.name)
    );
    dispatch({
      type: 'permissionManager/updateUser',
      payload: {
        email: selectedUser,
        roles: updatedRoles.filter((role) => role.id).map((role) => ({ id: role.id })),
      },
    });
  };

  useEffect(() => {
    if (!roles || roles.length === 0) {
      dispatch({ type: 'permissionManager/fetchRoles' });
    }
  }, [dispatch, roles]);

  useEffect(() => {
    if (isModalVisible === true) {
      dispatch({ type: 'permissionManager/fetchRoles' });
    }
  }, [isModalVisible, dispatch]);

  return (
    <PermissionManagerWrapper>
      <Typography variant={'h2'}>Permission Manager</Typography>
      <br />
      <>
        <Spin spinning={loading}>
          <GridLayout
            leftHandSpan={LEFT_HAND_SPAN}
            rightHandSpan={RIGHT_HAND_SPAN}
            headerChildren={
              <LeftSideHeaderWrapper>
                <ButtonWrapper icon="IconAdd" onClick={showModal}>
                  New User
                </ButtonWrapper>
              </LeftSideHeaderWrapper>
            }
            leftHandChildren={
              <div data-testid={PERMISSION_MANAGER_TABLE}>
                <LeftPanel
                  tableData={tableData}
                  permissionWidths={permissionWidths}
                  selectedUser={selectedUser}
                  handleSelectedUser={handleSelectedUser}
                  handleWidthChange={handleWidthChange}
                />
              </div>
            }
            rightHandChildren={
              selectedUser && (
                <div data-testid={PERMISSION_MANAGER_VIEW_ROLES}>
                  <FormWrapper id="DisplayRoles" form={form} onFinish={null}>
                    <Row gutter={16}>
                      <Typography variant="h5">Edit Roles for user {selectedUser}</Typography>
                    </Row>
                    <br />
                    <br />
                    <Row gutter={16}>
                      <Typography variant="h5">Roles</Typography>
                    </Row>
                    <Row gutter={16}>
                      <Input
                        placeholder="Search roles..."
                        prefix={<SearchOutlined />}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </Row>
                    <br />
                    <List
                      dataSource={filteredRoles}
                      renderItem={(role) => (
                        <List.Item
                          actions={[
                            <button type="button" onClick={() => handleDeleteRole(role)}>
                              <DeleteOutlined />
                            </button>,
                          ]}
                        >
                          {role}
                        </List.Item>
                      )}
                    />
                    <div style={{ display: 'flex', alignItems: 'center', marginTop: '16px' }}>
                      <Select
                        style={{ flex: 1, marginRight: '8px' }}
                        placeholder="Select a role"
                        value={selectedRole}
                        onChange={(value) => setSelectedRole(value)}
                      >
                        {(roles || []).map((role) => (
                          <Select.Option key={role.id} value={role.name}>
                            {role.name}
                          </Select.Option>
                        ))}
                      </Select>
                      <ButtonWrapper onClick={handleAddRole}>Add Role</ButtonWrapper>
                    </div>
                  </FormWrapper>
                </div>
              )
            }
          />
        </Spin>
      </>
      <NewUserModal
        isModalVisible={isModalVisible}
        setIsModalVisible={setIsModalVisible}
        dispatch={dispatch}
        onUserRolesUpdate={handleUserRolesUpdate}
      />
    </PermissionManagerWrapper>
  );
};

function mapStateToProps({ app, permissionManager, loading }) {
  return {
    permissions: app.currentUser.permissions,
    userData: permissionManager.userData,
    permissionWidths: permissionManager.permissionWidths,
    loading: loading.effects['permissionManager/fetchUsers'],
    roles: permissionManager.roles || [],
  };
}

export default connect(mapStateToProps)(ViewUsers);
